import { useCallback, useEffect, useState } from "react";
import { Inbox, KanbanSquare, CalendarDays, UserCheck, Building2, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, fmtErr, LEAD_STATUSES, LEAD_STATUS_LABELS, LEAD_STATUS_COLORS, LISTING_STATUS_COLORS, ROLE_LABELS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout, { StatCard, EmptyState, Th, Td } from "./DashboardLayout";
import { VisitBadge, VisitProposer, RescheduleControl } from "./VendorDashboard";
import VisitCalendar from "./VisitCalendar";
import ListingForm from "../../components/ListingForm";

export default function SalesDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("queue");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [pendingPartners, setPendingPartners] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [editingListing, setEditingListing] = useState(null);
  const [postFormKey, setPostFormKey] = useState(0);

  const loadStats = useCallback(() => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadLeads = useCallback(() => api.get("/leads").then(({ data }) => setLeads(data.leads)).catch(() => {}), []);
  const loadAssignable = useCallback(() => api.get("/leads/assignable").then(({ data }) => setAssignable(data.users)).catch(() => {}), []);
  const loadPartners = useCallback(() => api.get("/admin/users", { params: { status: "pending" } }).then(({ data }) => setPendingPartners(data.users.filter((u) => ["builder", "agent"].includes(u.role)))).catch(() => {}), []);
  const loadAllListings = useCallback(() => api.get("/admin/properties").then(({ data }) => setAllListings(data.properties)).catch(() => {}), []);

  useEffect(() => { loadStats(); loadLeads(); loadAssignable(); loadPartners(); }, [loadStats, loadLeads, loadAssignable, loadPartners]);
  useEffect(() => {
    if (tab === "listings") loadAllListings();
  }, [tab, loadAllListings]);

  const queue = leads.filter((l) => !l.assigned_to);
  const mine = leads.filter((l) => l.assigned_to === user?.id);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      loadLeads();
      loadStats();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const reload = () => { loadLeads(); loadStats(); };

  const partnerAction = async (id, status) => {
    let reason = "";
    if (status === "rejected") {
      reason = window.prompt("Rejection reason (shown to the applicant):");
      if (reason === null) return;
    }
    try {
      await api.post(`/admin/users/${id}/status`, { status, reason });
      toast.success(status === "active" ? "Partner approved — their desk is live" : "Application rejected");
      loadPartners();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const listingAction = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      loadAllListings();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const LeadTable = ({ rows, showAssign }) => (
    <div className="dash-card overflow-x-auto">
      {rows.length === 0 ? <EmptyState title="All clear" hint="New enquiries route here automatically." /> : (
        <table className="w-full min-w-[880px]">
          <thead><tr><Th>Lead</Th><Th>Property</Th><Th>Routing</Th><Th>Status</Th><Th>Note & Visit</Th><Th>{showAssign ? "Assign" : ""}</Th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                <Td><p className="font-medium text-black">{l.name}</p><p className="text-[11px] text-black/45">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p><p className="mt-1 max-w-[220px] text-[11px] italic text-black/50">"{l.message}"</p></Td>
                <Td>{l.property_title}<p className="text-[11px] text-black/45">{l.property_locality}</p></Td>
                <Td className="font-mono text-[10px] uppercase tracking-widest text-black/45">{l.routed_by_rule || "—"}</Td>
                <Td>
                  <select className="dash-input w-36" value={l.status} data-testid={`lead-status-${l.id}`} onChange={(e) => act(() => api.patch(`/leads/${l.id}`, { status: e.target.value }), "Status updated")}>
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>)}
                  </select>
                  <br />
                  <VisitBadge lead={l} />
                </Td>
                <Td>
                  <input className="dash-input w-44" placeholder="Add a note…" value={noteDrafts[l.id] || ""} onChange={(e) => setNoteDrafts({ ...noteDrafts, [l.id]: e.target.value })} data-testid={`lead-note-${l.id}`} />
                  {l.notes?.length > 0 && <p className="mt-1 text-[10px] text-black/40">Last: {l.notes[l.notes.length - 1].text}</p>}
                  <VisitProposer lead={l} onDone={reload} />
                  <RescheduleControl lead={l} onDone={reload} />
                </Td>
                <Td>
                  <div className="flex flex-col gap-2">
                    <button className="dash-btn-ghost" data-testid={`lead-note-save-${l.id}`} onClick={() => { if (noteDrafts[l.id]?.trim()) { act(() => api.patch(`/leads/${l.id}`, { note: noteDrafts[l.id].trim() }), "Note added"); setNoteDrafts({ ...noteDrafts, [l.id]: "" }); } }}>Save Note</button>
                    {showAssign && (
                      <select className="dash-input w-40" value="" data-testid={`lead-assign-${l.id}`} onChange={(e) => e.target.value && act(() => api.post(`/leads/${l.id}/assign`, { user_id: e.target.value }), "Lead assigned")}>
                        <option value="">Assign to…</option>
                        {assignable.map((a) => <option key={a.id} value={a.id}>{a.name} ({ROLE_LABELS[a.role]})</option>)}
                      </select>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <DashboardLayout
      title="Sales Desk"
      activeTab={tab}
      onTabChange={setTab}
      tabs={[
        { key: "queue", label: "Unassigned Queue", icon: Inbox, badge: queue.length },
        { key: "pipeline", label: "My Pipeline", icon: KanbanSquare },
        { key: "calendar", label: "Visit Calendar", icon: CalendarDays },
        { key: "partners", label: "Partner Requests", icon: UserCheck, badge: pendingPartners.length },
        { key: "listings", label: "Listing Review", icon: Building2 },
        { key: "post-listing", label: "Post Listing", icon: PlusCircle },
      ]}
    >
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="sales-stats">
        <StatCard testid="stat-queue" label="In Queue" value={queue.length} />
        <StatCard testid="stat-mine" label="My Active Leads" value={mine.filter((l) => !["won", "lost"].includes(l.status)).length} />
        <StatCard testid="stat-visits" label="Site Visits" value={stats?.my_leads?.site_visit || 0} />
        <StatCard testid="stat-won" label="Won" value={stats?.my_leads?.won || 0} />
      </div>

      {tab === "queue" && <div data-testid="unassigned-queue"><LeadTable rows={queue} showAssign /></div>}
      {tab === "pipeline" && <div data-testid="my-pipeline"><LeadTable rows={mine} showAssign={false} /></div>}
      {tab === "calendar" && <VisitCalendar leads={leads} />}

      {tab === "post-listing" && (
        <div data-testid="sales-post-listing">
          <ListingForm
            key={postFormKey}
            onDone={() => { setPostFormKey((k) => k + 1); setTab("listings"); loadAllListings(); }}
          />
          <p className="mt-3 max-w-3xl font-mono text-[10px] uppercase tracking-[0.15em] text-black/40">
            Sales-posted listings still enter review — approve them from Listing Review to publish
          </p>
        </div>
      )}

      {tab === "partners" && (
        <div className="dash-card overflow-x-auto" data-testid="partners-panel">
          {pendingPartners.length === 0 ? <EmptyState title="No pending applications" hint="New broker and developer applications land here for review." /> : (
            <table className="w-full min-w-[680px]">
              <thead><tr><Th>Applicant</Th><Th>Role</Th><Th>Office</Th><Th>Applied</Th><Th>Decision</Th></tr></thead>
              <tbody>
                {pendingPartners.map((u) => (
                  <tr key={u.id} data-testid={`partner-row-${u.id}`}>
                    <Td><p className="font-medium text-black">{u.name}</p><p className="text-[11px] text-black/45">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p></Td>
                    <Td>{ROLE_LABELS[u.role]}</Td>
                    <Td><p>{u.company || "—"}</p>{u.company_address && <p className="max-w-[220px] text-[11px] text-black/45">{u.company_address}</p>}</Td>
                    <Td>{new Date(u.created_at).toLocaleDateString("en-IN")}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button className="dash-btn-ghost" data-testid={`partner-approve-${u.id}`} onClick={() => partnerAction(u.id, "active")}>Approve</button>
                        <button className="dash-btn-ghost" data-testid={`partner-reject-${u.id}`} onClick={() => partnerAction(u.id, "rejected")}>Reject</button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "listings" && (
        <div data-testid="sales-listings-panel">
          {editingListing ? (
            <ListingForm
              key={editingListing.id}
              initial={editingListing}
              showProject={editingListing.owner_role === "builder"}
              onDone={() => { setEditingListing(null); loadAllListings(); }}
              onCancel={() => setEditingListing(null)}
            />
          ) : (
            <div className="dash-card overflow-x-auto">
              {allListings.length === 0 ? <EmptyState title="No listings" hint="Listings from every seller appear here for review." /> : (
                <table className="w-full min-w-[820px]">
                  <thead><tr><Th>Listing</Th><Th>Owner</Th><Th>Price</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
                  <tbody>
                    {allListings.map((p) => (
                      <tr key={p.id} data-testid={`sales-listing-row-${p.id}`}>
                        <Td><p className="font-medium text-black">{p.title}</p><p className="text-[11px] text-black/45">{p.type} · {p.locality} · {p.beds} bed</p>
                          {p.status === "rejected" && p.rejection_reason && <p className="mt-1 text-[11px] text-red-600">Rejected: {p.rejection_reason}</p>}
                        </Td>
                        <Td><p>{p.owner_name}</p><p className="text-[11px] text-black/45">{ROLE_LABELS[p.owner_role] || p.owner_role}</p></Td>
                        <Td className="font-medium">{formatINR(p.price, p.price_unit)}</Td>
                        <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LISTING_STATUS_COLORS[p.status]}`}>{p.status.replace("_", " ")}</span></Td>
                        <Td>
                          <div className="flex flex-wrap gap-2">
                            {p.status !== "published" && (
                              <button className="dash-btn-ghost" data-testid={`sales-approve-listing-${p.id}`} onClick={() => listingAction(() => api.post(`/admin/properties/${p.id}/verify`, { action: "approve" }), "Listing published")}>Approve</button>
                            )}
                            {p.status !== "rejected" && (
                              <button className="dash-btn-ghost" data-testid={`sales-reject-listing-${p.id}`} onClick={() => {
                                const reason = window.prompt("Rejection reason (shown to the seller):");
                                if (reason !== null) listingAction(() => api.post(`/admin/properties/${p.id}/verify`, { action: "reject", reason }), "Listing rejected");
                              }}>Reject</button>
                            )}
                            <button className="dash-btn-ghost" data-testid={`sales-edit-listing-${p.id}`} onClick={() => setEditingListing(p)}>Edit</button>
                            <button className="dash-btn-ghost" data-testid={`sales-delete-listing-${p.id}`} onClick={() => {
                              if (window.confirm(`Delete "${p.title}" permanently?`)) listingAction(() => api.delete(`/admin/properties/${p.id}`), "Listing deleted");
                            }}>Delete</button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
